import { App, Alert, Button, Checkbox, Flex, Space, Tooltip, Typography } from "antd";
import { InfoCircleOutlined, PlusOutlined, ThunderboltOutlined, WarningOutlined } from "@ant-design/icons";
import CurrencyAmountInput from "src/components/CurrencyAmountInput";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSWRConfig } from "swr";
import CurrencyAmount from "src/components/CurrencyAmount";
import { WalletBalanceDisplay } from "src/components/wallet/WalletBalanceDisplay";
import ShebaDisplay from "src/components/wallet/ShebaDisplay";
import { EarlyPaymentInfoModal } from "src/components/wallet/EarlyPaymentInfo";
import EarlyPaymentBreakdown from "src/components/wallet/EarlyPaymentBreakdown";
import FirstEarlyPaymentBadge from "src/components/wallet/FirstEarlyPaymentBadge";
import useUser from "src/hooks/useUser";
import useIsDesktop from "src/hooks/useIsDesktop";
import useOrg from "src/hooks/apis/useOrg";
import useWithdrawTransaction from "src/hooks/apis/useWithdrawTransaction";
import useEarlyPaymentStatus from "src/hooks/apis/useEarlyPaymentStatus";
import useEarlyPaymentWithdraw from "src/hooks/apis/useEarlyPaymentWithdraw";
import useEarlyPaymentToggle from "src/hooks/apis/useEarlyPaymentToggle";
import { toLatinDigit } from "src/utils/digits";
import {
  calcFeeAmount,
  calcTotalFee,
  amountAfterFeeEqualsBalance,
} from "src/utils/transactionFees";
import ResponsiveModal from "src/components/ResponsiveModal";
import { trackWithdrawRequest } from "src/utils/gtm";
import { IS_WHITE_LABEL } from "src/constants";

const parseNumber = (value: string) => {
  const raw = toLatinDigit(value.replace(/[٬,]/g, ""));
  return parseInt(raw, 10);
}

const EP_ERROR_MESSAGES: Record<string, string> = {
  ACTIVE_LOAN_EXISTS: "یک وام فعال دارید — پرداخت زودهنگام ممکن نیست",
  NO_ACTIVE_CHANNEL: "کانال فعال ندارید",
  NO_INCOME_HISTORY: "سابقه درآمد ثبت نشده است",
  BLOCKED_BY_DEFAULT: "به دلیل نکول وام قبلی، امکان پرداخت زودهنگام وجود ندارد",
  INSUFFICIENT_LIMIT: "مبلغ پرداخت زودهنگام بیشتر از سقف مجاز است",
  EARLY_PAYMENT_DISABLED: "پرداخت زودهنگام غیرفعال است",
};

function IrtWithdrawForm() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { mutate } = useSWRConfig();
  const { data: user } = useUser();
  const { data: org } = useOrg();
  const isWhiteLabel = org?.config?.whiteLabel ?? IS_WHITE_LABEL;
  const { data: epStatus } = useEarlyPaymentStatus();
  const isDesktop = useIsDesktop();
  const withdrawMutation = useWithdrawTransaction();
  const epWithdrawMutation = useEarlyPaymentWithdraw();
  const epToggleMutation = useEarlyPaymentToggle();

  const [tomanAmount, setTomanAmount] = useState("");
  const [irtError, setIrtError] = useState("");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingAmount, setPendingAmount] = useState<number | null>(null);
  const [epInfoOpen, setEpInfoOpen] = useState(false);
  const [enableAutoEp, setEnableAutoEp] = useState(false);

  const { irtFee, withdrawalsEnabled, irtWithdrawalsEnabled, gbpToIrt = 0 } = org?.config ?? {};
  const gbpCredit = user?.gbpCredit ?? 0;
  const balanceIrt = gbpCredit * gbpToIrt;
  const sheba = user?.bankAccount?.sheba ? `IR${user.bankAccount.sheba}` : null;

  const epAvailable = epStatus?.available && (epStatus?.estimatedAmount ?? 0) > 0;
  const epEstimatedGbp = epStatus?.estimatedAmount ?? 0;
  // Use the effective fee (discounted for a first early payment) so the breakdown
  // matches what the server will actually charge.
  const epFeePercent = epStatus?.effectiveFeePercent ?? epStatus?.feePercent ?? 0;
  const maxGbp = epAvailable ? gbpCredit + epEstimatedGbp : gbpCredit;
  const maxIrt = maxGbp * gbpToIrt;
  const epIrt = epEstimatedGbp * gbpToIrt;

  const hasInsufficientCredit = gbpCredit < 30 && !epAvailable;
  const minWithdrawAmount = 30 * gbpToIrt;
  const formDisabled = !withdrawalsEnabled || !irtWithdrawalsEnabled;

  const amountIrt = tomanAmount ? parseNumber(tomanAmount) : 0;
  const transactionFee = calcFeeAmount(amountIrt, irtFee?.transaction);
  const withdrawFee = calcFeeAmount(amountIrt, irtFee?.withdraw);
  const totalFee = irtFee && amountIrt > 0 ? calcTotalFee({ amount: amountIrt, fee: irtFee }) : 0;
  const isFree = withdrawFee === 0;

  // Early payment portion calculation
  const amountGbp = gbpToIrt > 0 ? amountIrt / gbpToIrt : 0;
  const loanPortionGbp = Math.max(0, amountGbp - gbpCredit);
  const needsEarlyPayment = epAvailable && loanPortionGbp > 0.01;
  const epFeeGbp = needsEarlyPayment
    ? Math.ceil(loanPortionGbp * epFeePercent) / 100
    : 0;
  // The advance/fee/repayment are GBP-denominated (repaid from GBP income), so the
  // breakdown shows them in GBP. Only the withdrawal request itself is in IRT.
  const fromBalanceGbp = Math.max(0, amountGbp - loanPortionGbp);

  const safeBalance = Number.isFinite(balanceIrt) ? balanceIrt : 0;
  const maxWithdrawIrt =
    irtFee && safeBalance > 0
      ? amountAfterFeeEqualsBalance({ balance: safeBalance, fee: irtFee })
      : Math.floor(safeBalance);

  const maxWithdrawWithEp = epAvailable
    ? Math.floor(maxIrt)
    : maxWithdrawIrt;

  const handleSubmitClick = () => {
    const numValue = parseNumber(tomanAmount);

    if (!tomanAmount || isNaN(numValue)) {
      setIrtError("لطفا مبلغ را وارد کنید");
      return;
    }
    if (numValue < minWithdrawAmount) {
      setIrtError(`حداقل مبلغ برداشت ${minWithdrawAmount.toLocaleString("fa-IR")} تومان می‌باشد`);
      return;
    }
    if (numValue > maxWithdrawWithEp) {
      setIrtError("مبلغ بیشتر از موجودی قابل برداشت است");
      return;
    }
    setIrtError("");
    setPendingAmount(numValue);
    setConfirmModalOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (pendingAmount == null) return;
    try {
      if (needsEarlyPayment) {
        const result = await epWithdrawMutation.trigger({ amount: pendingAmount, currency: "IRT" });
        trackWithdrawRequest({
          transaction_id: result?.withdrawal?.id,
          currency: "IRT",
          withdraw_amount: pendingAmount,
          available_balance: safeBalance,
          early_income_credit: epAvailable ? epIrt : 0,
          used_early_income: true,
          early_income_used_amount: Math.max(0, pendingAmount - safeBalance),
        });
        // Turn on auto early payment together with the withdrawal request, only
        // after the user confirms in the modal (not immediately on checkbox toggle).
        if (enableAutoEp && !epStatus?.autoEnabled) {
          await epToggleMutation.trigger({ enabled: true });
        }
        if (result?.loan) {
          message.success("درخواست برداشت و پرداخت زودهنگام با موفقیت ثبت شد — در انتظار تأیید ادمین");
        } else {
          message.success("درخواست برداشت با موفقیت ثبت شد");
        }
      } else {
        const result = await withdrawMutation.trigger({ amount: pendingAmount, currency: "IRT" });
        trackWithdrawRequest({
          transaction_id: result?.id,
          currency: "IRT",
          withdraw_amount: pendingAmount,
          available_balance: safeBalance,
          early_income_credit: epAvailable ? epIrt : 0,
          used_early_income: false,
          early_income_used_amount: 0,
        });
        message.success("درخواست برداشت با موفقیت ثبت شد");
      }
      mutate((key) => typeof key === "string" && key.startsWith("transactions-"));
      mutate("user");
      mutate("early-payment-status");
      mutate("loans");
      setConfirmModalOpen(false);
      setPendingAmount(null);
      setTomanAmount("");
      setEnableAutoEp(false);
      navigate("/finance");
    } catch (err: any) {
      const code = err?.response?.data?.error;
      message.error(EP_ERROR_MESSAGES[code] || "خطا در ثبت درخواست برداشت");
    }
  };

  const isMutating =
    withdrawMutation.isMutating || epWithdrawMutation.isMutating || epToggleMutation.isMutating;

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {formDisabled && (
        <Alert
          banner
          title="برداشت تومان در حال حاضر غیر فعال است.."
          type="warning"
          showIcon
          icon={<WarningOutlined />}
        />
      )}
      <div className="relative w-full">
        <div
          className={
            formDisabled
              ? "flex flex-col gap-4 select-none blur-[3px] opacity-60 transition-[filter,opacity]"
              : "flex flex-col gap-4"
          }
          aria-hidden={formDisabled}
        >
          <WalletBalanceDisplay
            primaryAmount={
              epAvailable
                ? Math.floor(maxIrt).toLocaleString("en-US", { maximumFractionDigits: 0 })
                : balanceIrt.toLocaleString("en-US", { maximumFractionDigits: 0 })
            }
            primaryUnit="تومان"
            heroColor={
              isWhiteLabel
                ? org?.theme?.walletHeroColor || org?.theme?.primaryColor
                : undefined
            }
            rateLabel="نرخ فعلی پوند: "
            rateAmount={gbpToIrt.toLocaleString("en-US", {
              maximumFractionDigits: 0,
            })}
          >
            {epAvailable && (
              <div className="mt-3">
                {/* Credit + early payment together on one small, muted row */}
                <Flex gap={32} wrap="wrap">
                  <div>
                    <Typography.Text type="secondary" className="block text-xs">دارایی</Typography.Text>
                    <CurrencyAmount amount={Math.floor(balanceIrt)} currency="IRT" fontSize={13} />
                  </div>
                  <div>
                    <Typography.Text type="secondary" className="block text-xs">
                      <ThunderboltOutlined className="ml-1 text-amber-500" />
                      پرداخت زودهنگام
                      <Tooltip title="درباره‌ی پرداخت زودهنگام">
                        <InfoCircleOutlined
                          className="mr-1 cursor-pointer opacity-60 hover:opacity-100"
                          onClick={() => setEpInfoOpen(true)}
                        />
                      </Tooltip>
                    </Typography.Text>
                    <CurrencyAmount amount={Math.floor(epIrt)} currency="IRT" fontSize={13} />
                    <FirstEarlyPaymentBadge className="mt-1 mx-1" />
                  </div>
                </Flex>
                {/* Total below, label above value */}
                <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-2">
                  <Typography.Text type="secondary" className="block text-xs">مجموع (معادل پوند)</Typography.Text>
                  <CurrencyAmount amount={maxGbp} currency="GBP" fontSize={16} />
                </div>
              </div>
            )}
          </WalletBalanceDisplay>
          <div>
            <Typography.Text strong className="block mb-1">
              واریز به
            </Typography.Text>
            <ShebaDisplay sheba={sheba} />
          </div>
          <div>
            <Flex justify="space-between" className="mb-1">
              <Typography.Text type="secondary">مقدار برداشت به تومان</Typography.Text>
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                className="p-0"
                onClick={() => setTomanAmount(String(maxWithdrawWithEp))}
              >
                حد اکثر مبلغ
              </Button>
            </Flex>
            <CurrencyAmountInput
              placeholder={`حداقل ${minWithdrawAmount.toLocaleString("en-US")}`}
              currencyLabel="تومان"
              value={tomanAmount ? Number(tomanAmount).toLocaleString("en-US") : ""}
              status={irtError ? "error" : ""}
              onChange={(value) => {
                const toLatin = toLatinDigit(value.replace(/[٬,]/g, ""));
                if (toLatin === "" || (!isNaN(Number(toLatin)) && Number(toLatin) <= 10_000_000_000)) {
                  setTomanAmount(toLatin);
                  setIrtError("");
                }
              }}
            />
            {irtError && (
              <Typography.Text type="danger" className="text-xs mt-1 block">
                {irtError}
              </Typography.Text>
            )}
          </div>

          {/* Early payment breakdown */}
          {needsEarlyPayment && amountIrt > 0 && (
            <EarlyPaymentBreakdown
              fromBalance={fromBalanceGbp}
              loanPortion={loanPortionGbp}
              epFee={epFeeGbp}
              feePercent={epFeePercent}
              currency="GBP"
            />
          )}

          {needsEarlyPayment && amountIrt > 0 && !epStatus?.autoEnabled && (
            <Checkbox
              checked={enableAutoEp}
              onChange={(e) => setEnableAutoEp(e.target.checked)}
            >
              <Typography.Text className="text-sm">
                {/* <ThunderboltOutlined className="ml-1 text-amber-500" /> */}
                دریافت خودکار درآمد هر ماه به‌صورت پرداخت زودهنگام
              </Typography.Text>
            </Checkbox>
          )}

          {!needsEarlyPayment && (
            <>
              {isFree ? null : (
                <Flex justify="space-between" className="text-sm">
                  <Typography.Text type="secondary">کارمزد برداشت</Typography.Text>
                  <CurrencyAmount amount={Number.isFinite(withdrawFee) ? withdrawFee : 0} currency="IRT" />
                </Flex>
              )}
              {transactionFee > 0 && Number.isFinite(transactionFee) && (
                <Flex justify="space-between" className="text-sm">
                  <Typography.Text type="secondary">هزینه انتقال</Typography.Text>
                  <CurrencyAmount amount={transactionFee} currency="IRT" />
                </Flex>
              )}
            </>
          )}

          <Button
            type="primary"
            size="large"
            block={!isDesktop}
            className="self-start"
            loading={isMutating}
            disabled={hasInsufficientCredit || !gbpToIrt || formDisabled}
            onClick={handleSubmitClick}
          >
            درخواست برداشت
          </Button>
        </div>
        {formDisabled && (
          <div
            className="absolute inset-0 z-10 cursor-not-allowed"
            aria-hidden
          />
        )}
      </div>
      <ResponsiveModal
        title={needsEarlyPayment ? "تأیید برداشت با پرداخت زودهنگام" : "تأیید برداشت"}
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setPendingAmount(null);
        }}
        onOk={handleConfirmSubmit}
        okText={needsEarlyPayment ? "تأیید و ثبت درخواست" : "تأیید و برداشت"}
        cancelText="انصراف"
        confirmLoading={isMutating}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Typography.Text className="rtl text-right text-base block">
            {needsEarlyPayment
              ? "درخواست برداشت همراه با پرداخت زودهنگام ثبت می‌شود و پس از تأیید، پردازش خواهد شد."
              : "آیا از ثبت درخواست برداشت اطمینان دارید؟"}
          </Typography.Text>
          {pendingAmount != null && (
            <>
              <Typography.Text className="rtl text-right text-base">
                مبلغ برداشت:{" "}
                <strong><CurrencyAmount amount={pendingAmount} currency="IRT" /></strong>
              </Typography.Text>
              {/* {needsEarlyPayment && (
                <Typography.Text type="secondary" className="text-sm">
                  <InfoCircleOutlined className="ml-1" />
                  بازپرداخت پرداخت زودهنگام از درآمد کانال شما در ماه بعد به‌طور خودکار انجام می‌شود.
                </Typography.Text>
              )} */}
              {!needsEarlyPayment && totalFee > 0 && (
                <Typography.Text className="rtl text-right text-base">
                  کارمزد:{" "}
                  <strong><CurrencyAmount amount={totalFee} currency="IRT" /></strong>
                </Typography.Text>
              )}
              {needsEarlyPayment && enableAutoEp && !epStatus?.autoEnabled && (
                <Typography.Text type="secondary" className="text-sm block">
                  <ThunderboltOutlined className="ml-1 text-amber-500" />
                  دریافت خودکار درآمد هر ماه نیز فعال می‌شود.
                </Typography.Text>
              )}
            </>
          )}
        </Space>
      </ResponsiveModal>
      <EarlyPaymentInfoModal
        open={epInfoOpen}
        onClose={() => setEpInfoOpen(false)}
        feePercent={epStatus?.feePercent}
        percent={epStatus?.percent}
        firstFeePercent={epStatus?.firstEarlyPaymentFeePercent}
        firstEligible={epStatus?.firstEarlyPaymentEligible}
      />
    </Space>
  );
}

export default IrtWithdrawForm;
